import { ThemeProvider } from "@mui/material";
import MaterialTable from "material-table";
import React, { useState, useEffect } from "react";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { icons } from "../../../components/Utilities/UtilitiesWD";
import { getDeliveryRequestAll, normalizeDate } from "../../../utilities";
import { CSVLink } from "react-csv";
import { useAppMuiTheme } from "../../../theme/MuiThemeBridge";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import { LoadingState, StatTile } from "../../../components/UI/States";
import {
  Pill,
  mtComponents,
  mtHeaderStyle,
  mtSearchFieldStyle,
  mtStyle,
} from "../../../components/WindowDoor/wdUi";

const RequestSummary = (props) => {
  const [requests, setRequests] = useState(null);
  const defaultMaterialTheme = useAppMuiTheme();

  useEffect(() => {
    getDeliveryRequestAll(setRequests);
  }, []);

  const countStatus = (status) =>
    Array.isArray(requests)
      ? requests.filter((row) => row.requestStatus === status).length
      : 0;
  const loaded = Array.isArray(requests);

  return (
    <PageShell size="2xl">
      <PageHeader
        icon={SummarizeOutlinedIcon}
        tone="indigo"
        eyebrow="Windows & Doors"
        title="Exterior Order Request Summary"
        description="All exterior delivery requests across the company."
        actions={
          requests?.length > 0 && (
            <CSVLink
              target="_blank"
              data={requests}
              className="app-btn app-btn-secondary"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
              Download CSV
            </CSVLink>
          )
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Requests Made"
          value={loaded ? requests.length : "—"}
        />
        <StatTile
          label="Pending"
          value={loaded ? countStatus(0) : "—"}
          tone="amber"
        />
        <StatTile
          label="Scheduled"
          value={loaded ? countStatus(1) : "—"}
          tone="emerald"
        />
        <StatTile
          label="Rejected"
          value={loaded ? countStatus(2) : "—"}
          tone="rose"
        />
      </div>

      {requests ? (
        <div className="app-card overflow-hidden rounded-2xl border">
          <ThemeProvider theme={defaultMaterialTheme}>
            <MaterialTable
              components={mtComponents}
              style={mtStyle}
              options={{
                pageSize: 10, // make initial page size
                emptyRowsWhenPaging: false, // To avoid of having empty rows
                pageSizeOptions: [5, 10, 15, 25],
                headerStyle: { search: true, ...mtHeaderStyle },
                searchFieldStyle: mtSearchFieldStyle,
              }}
              columns={[
                {
                  title: "Date",
                  field: "requestSubmitted",
                  render: (rowData) => {
                    return normalizeDate(new Date(rowData?.requestSubmitted));
                  },
                },
                {
                  title: "From",
                  field: "requestName",
                },
                {
                  title: "Status",
                  field: "requestStatus",
                  render: (rowData) => {
                    switch (rowData.requestStatus) {
                      case 0:
                        return <Pill tone="amber">Pending</Pill>;
                      case 1:
                        return (
                          <Pill tone="emerald">
                            Scheduled for{" "}
                            {rowData.earliestDateScheduled
                              ? rowData.earliestDateScheduled
                              : rowData.requestDate}
                          </Pill>
                        );
                      case 2:
                        return <Pill tone="rose">Rejected</Pill>;
                      case 3:
                        return <Pill>Deleted</Pill>;
                      default:
                        return <Pill>Unknown Status</Pill>;
                    }
                  },
                },
                {
                  title: "Customer",
                  field: "customer",
                },
              ]}
              data={requests}
              title={"All Exterior Delivery Requests"}
              icons={icons}
              localization={{
                body: {
                  emptyDataSourceMessage: "No records to display",
                },
              }}
            />
          </ThemeProvider>
        </div>
      ) : (
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading requests…" />
        </div>
      )}
    </PageShell>
  );
};

export default RequestSummary;
